
import React, { useState, useEffect } from 'react';
import { useCommunity } from '@/contexts/CommunityContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '@/components/ui/table';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Search, ShieldCheck, AlertTriangle, Loader2, UserX, Edit, ChevronLeft, ChevronRight, UserCog } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ui/status-badge';
import { useIsMobile } from '@/hooks/use-mobile';
import UserProfileSheet from '@/components/admin/UserProfileSheet';
import { useToast } from '@/hooks/use-toast';
import { useUserProfiles, ExtendedCommunityMember, COMMUNITY_MEMBERS_QUERY_KEY } from '@/hooks/useUserProfiles';
import { useCommunityMembers } from '@/hooks/useCommunityMembers';
import { useQueryClient } from '@tanstack/react-query';
import { UserRole } from '@/contexts/CommunityMemberContext';
import { CommunityMemberStatus } from '@/types/community';
import { AdminPagination } from '@/components/admin/AdminPagination';

const UserManagement = () => {
  const { currentCommunity } = useCommunity();
  const { blockMember, unblockMember, updateMemberRole } = useCommunityMembers(currentCommunity.id);
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  const { members, totalCount, totalPages, isLoading, error, refetch } = useUserProfiles(currentCommunity.id, searchTerm, currentPage, pageSize);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);
  const [selectedUser, setSelectedUser] = useState<ExtendedCommunityMember | null>(null);
  const [updatingRoles, setUpdatingRoles] = useState<Record<string, boolean>>({});
  const isMobile = useIsMobile();
  const { toast } = useToast();

  // Effect to refetch data after any mutations
  useEffect(() => {
    // This will ensure synchronization between the two hooks
    if (blockMember.isSuccess || unblockMember.isSuccess) {
      refetch();
    }
  }, [blockMember.isSuccess, unblockMember.isSuccess, refetch]);

  
  const handleBlockMember = async (memberId: string) => {
    try {
      await blockMember.mutateAsync(memberId);
      // If this was the selected user in the sheet, close it
      if (selectedUser && selectedUser.id === memberId) {
        setSelectedUser(null);
      }
      toast({
        title: "User blocked",
        description: "The user has been blocked from the community."
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to block user from community"
      });
    }
  };

  const handleUnblockMember = async (memberId: string) => {
    try {
      await unblockMember.mutateAsync(memberId);
      toast({
        title: "User unblocked",
        description: "The user has been unblocked and can access the community again."
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to unblock user"
      });
    }
  };

  const handleRoleChange = async (memberId: string, newRole: UserRole) => {
    setUpdatingRoles(prev => ({ ...prev, [memberId]: true }));
    
    updateMemberRole.mutate(
      { memberId, roleName: newRole },
      {
        onSuccess: () => {
          toast({
            title: "Role updated",
            description: "User role has been updated successfully"
          });
          setUpdatingRoles(prev => ({ ...prev, [memberId]: false }));
        },
        onError: (error: any) => {
          toast({
            variant: "destructive",
            title: "Error",
            description: error.message || "Failed to update user role"
          });
          setUpdatingRoles(prev => ({ ...prev, [memberId]: false }));
        }
      }
    );
  };
  
  // Function to format role name for display
  const formatRoleName = (roleName: string) => {
    return roleName.charAt(0).toUpperCase() + roleName.slice(1);
  };

  // Function to render role selection
  const renderRoleSelector = (member: ExtendedCommunityMember) => {
    const isUpdating = updatingRoles[member.id];
    
    return (
      <Select 
        value={member.role_name}
        disabled={isUpdating}
        onValueChange={(value: UserRole) => handleRoleChange(member.id, value)}
      >
        <SelectTrigger className="w-auto border-0 p-0 h-auto bg-transparent hover:bg-muted/50 transition-colors">
          <SelectValue>
            {isUpdating ? (
              <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-muted">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span className="text-xs">Updating...</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-muted hover:bg-muted/80 transition-colors cursor-pointer">
                <span className="text-xs font-medium">{formatRoleName(member.role_name)}</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="user">User</SelectItem>
          <SelectItem value="reviewer">Reviewer</SelectItem>
          <SelectItem value="admin">Admin</SelectItem>
        </SelectContent>
      </Select>
    );
  };

  // Function to render status badge
  const renderStatusBadge = (status: CommunityMemberStatus = 'ACTIVE') => {
    const isActive = status === 'ACTIVE';
    return (
      <StatusBadge 
        isActive={isActive}
        activeText="Active"
        inactiveText="Blocked"
      />
    );
  };

  // Function to render member actions
  const renderMemberActions = (member: ExtendedCommunityMember) => {
    const memberStatus = member.status || 'ACTIVE';
    
    if (memberStatus === 'BLOCKED') {
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleUnblockMember(member.id)}
          className="text-green-600 hover:text-green-700"
        >
          <ShieldCheck className="h-4 w-4 mr-1" /> Unblock
        </Button>
      );
    }

    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 text-destructive hover:bg-destructive/10"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <UserX className="h-4 w-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will block the user from accessing this community. They will not be able to rejoin until an administrator unblocks them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleBlockMember(member.id)}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };
  
  
  // Function to render user as a card for mobile view
  const renderMemberCard = (member: ExtendedCommunityMember) => (
    <Card key={member.id} className="mb-4 group">
      <CardContent className="pt-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
          <div className="mb-3 sm:mb-0" onClick={() => setSelectedUser(member)}>
            <div className="font-medium">
              {member.user_profile?.firstname || 'Unknown'} {member.user_profile?.lastname || ''}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {member.user_profile?.email || 'No email provided'}
            </div>
            <div className="text-sm text-muted-foreground">
              {member.user_profile?.town && member.user_profile?.country 
                ? `${member.user_profile.town}, ${member.user_profile.country}`
                : 'No location provided'
              }
            </div>
            <div className="flex gap-2 mt-2">
              {renderRoleSelector(member)}
              {renderStatusBadge(member.status)}
            </div>
          </div>
        </div>
        
        <div className="flex justify-end mt-4">
          {renderMemberActions(member)}
        </div>
      </CardContent>
    </Card>
  );
  
  
  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      <div className="relative">
        <h1 className="admin-section-title mb-2">User Management</h1>
        <p className="text-muted-foreground/80 text-lg">
          Manage community members and their permissions
        </p>
      </div>
      
      <Card className="admin-card admin-card-stats">
        <CardHeader className="pb-6">
          <CardTitle className="text-xl font-bold text-slate-800">Community Members</CardTitle>
          <CardDescription className="text-muted-foreground/80 mt-2">
            Manage members of {currentCommunity.name} community
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="flex space-x-2">
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
              </div>
            </div>
          ) : error && !isLoading ? (
            <div className="text-destructive p-4 rounded-md bg-destructive/10">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2" />
                <span>Error loading members</span>
              </div>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              No members found for this community.
            </div>
          ) : isMobile ? (
            // Mobile card view
            <div className="space-y-4">
              {members.map((member, index) => (
                <Card key={member.id} className={`admin-card admin-card-action animate-fade-in stagger-${Math.min(index + 1, 5)}  group`}>
                  <CardContent className="pt-6 overflow-visible">
                    <div className="mb-3" onClick={() => setSelectedUser(member)}>
                      <div className="flex justify-between items-start mb-3 gap-4">
                        <div className="font-semibold text-slate-800">
                          {member.user_profile?.firstname || 'Unknown'} {member.user_profile?.lastname || ''}
                        </div>
                        <div className="flex-shrink-0">
                          {renderStatusBadge(member.status)}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm text-muted-foreground/80">
                          {member.user_profile?.email || 'No email provided'}
                        </div>
                        <div className="text-sm text-muted-foreground/70">
                          {member.user_profile?.town && member.user_profile?.country 
                            ? `${member.user_profile.town}, ${member.user_profile.country}`
                            : 'No location provided'
                          }
                        </div>
                        <div className="text-sm text-muted-foreground/70">
                          Joined: {new Date(member.joined_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center gap-2 mt-4 pt-4 border-t border-gray-100 select-none" onClick={(e) => e.stopPropagation()}>
                      <div className="flex-1">
                        {renderRoleSelector(member)}
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUser(member);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {renderMemberActions(member)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            // Desktop table view
            <div className="rounded-xl border border-gray-200/60 overflow-hidden bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-100 bg-gray-50/50">
                    <TableHead className="font-semibold text-slate-700 h-12">Name</TableHead>
                    <TableHead className="font-semibold text-slate-700">Email</TableHead>
                    <TableHead className="font-semibold text-slate-700">Location</TableHead>
                    <TableHead className="font-semibold text-slate-700">Joined</TableHead>
                    <TableHead className="font-semibold text-slate-700">Status</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow 
                      key={member.id}
                      className="cursor-pointer hover:bg-gray-50/80 border-b border-gray-100/50 transition-colors"
                      onClick={() => setSelectedUser(member)}
                    >
                      <TableCell className="font-medium">
                        {member.user_profile?.firstname || 'Unknown'} {member.user_profile?.lastname || ''}
                      </TableCell>
                      <TableCell>
                        {member.user_profile?.email || 'No email'}
                      </TableCell>
                      <TableCell>
                        {member.user_profile?.town && member.user_profile?.country ? 
                          `${member.user_profile.town}, ${member.user_profile.country}` : 
                          'No location provided'}
                      </TableCell>
                      <TableCell>
                        {new Date(member.joined_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {renderStatusBadge(member.status)}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          {renderRoleSelector(member)}
                          {renderMemberActions(member)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <AdminPagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </CardContent>
      </Card>

      {selectedUser && (
        <UserProfileSheet
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          user={selectedUser}
          onBlock={handleBlockMember}
          onUnblock={handleUnblockMember}
          onRoleUpdate={handleRoleChange}
        />
      )}
    </div>
  );
};

export default UserManagement;
